"use client"

import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { clsx } from "@/src/utils/clsx"
import { getGCGeneralLinkHref } from "@/src/utils/links/getGCGeneralLinkHref"
import {
    type NavigationFeaturedLink,
    type NavigationGlobalLink
} from "@/types/sanity/header-config"

export type MenuLinkItem = NavigationGlobalLink | NavigationFeaturedLink

type MenuLinkListProps = {
    links: MenuLinkItem[] | undefined
    className?: string
    linkClassName?: string
    onNavigate?: () => void
    showArrow?: boolean
    /**
     * Renders the list as part of the desktop mega-menu: items become
     * `role="menuitem"` and join the panel's roving tabindex (single tab stop).
     * Omit for the mobile menu, where links stay in the natural tab order.
     */
    asMenuItem?: boolean
}

const BASE_LINK_CLASSES =
    "flex items-center h-24 text-col-text-tertiary Trim_Mono_Medium_POC_Text_12_130_-5_500_uppercase"

const ARROW_CLASS = clsx(
    "h-12 w-12 shrink-0",
    "translate-x-[1px] opacity-0",
    "transition-[opacity,transform] duration-200 ease-out",
    "group-hover:translate-x-[6px] group-hover:opacity-100",
    "motion-reduce:transition-none"
)

export default function MenuLinkList({
    links,
    className,
    linkClassName,
    onNavigate,
    showArrow,
    asMenuItem
}: MenuLinkListProps) {
    if (!links || links.length === 0) return null

    return (
        <ul
            role={asMenuItem ? "menu" : undefined}
            className={clsx("flex flex-col items-start", className)}
        >
            {links.map(link => {
                const href = link.link ? getGCGeneralLinkHref(link.link) : undefined
                const colorStyle = link.textColor?.hex
                    ? { color: link.textColor.hex }
                    : undefined
                return (
                    <li key={link._key} role={asMenuItem ? "menuitem" : undefined}>
                        {href ? (
                            <DynamicLink
                                href={href}
                                prefetch={false}
                                tabIndex={asMenuItem ? -1 : undefined}
                                className={clsx(BASE_LINK_CLASSES, linkClassName, {
                                    group: showArrow
                                })}
                                style={colorStyle}
                                onClick={onNavigate}
                            >
                                {showArrow ? (
                                    <>
                                        <span className="pt-2">{link.label}</span>
                                        <IconArrowRight
                                            className={ARROW_CLASS}
                                            ariaLabel=""
                                        />
                                    </>
                                ) : (
                                    link.label
                                )}
                            </DynamicLink>
                        ) : (
                            <span
                                className={clsx(BASE_LINK_CLASSES, linkClassName)}
                                style={colorStyle}
                            >
                                {link.label}
                            </span>
                        )}
                    </li>
                )
            })}
        </ul>
    )
}
